/**
 * Formato do que a API de chamados devolve.
 *
 * Declarado aqui, e não importado de `@prisma/client`, porque o front não
 * depende do banco: o que atravessa a rede é JSON, onde `Date` já virou string
 * ISO. Copiar o tipo do Prisma daria a impressão errada de que `openedAt` é um
 * `Date` e o `.getTime()` só falharia em execução.
 */

export const TIPOS_DE_CHAMADO = ['INCIDENT', 'REQUEST', 'PROBLEM', 'CHANGE', 'TASK'] as const;

export type Coverage = 'COVERED' | 'OUT_OF_TERM' | 'OUT_OF_SCOPE' | 'BILLABLE' | 'NOT_APPLICABLE';
export type TipoDeChamado = (typeof TIPOS_DE_CHAMADO)[number];

export const NIVEIS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type Nivel = (typeof NIVEIS)[number];

export const CATEGORIAS_DE_STATUS = [
  'NEW',
  'IN_PROGRESS',
  'PENDING',
  'RESOLVED',
  'CLOSED',
  'CANCELLED',
] as const;
export type CategoriaDeStatus = (typeof CATEGORIAS_DE_STATUS)[number];

export interface Chamado {
  id: string;
  number: string;
  type: TipoDeChamado;
  subject: string;
  descriptionHtml: string | null;
  descriptionText: string | null;

  parentId: string | null;
  rootId: string;
  path: string;
  depth: number;
  childCount: number;
  openChildCount: number;
  progressPct: number;

  requesterId: string;
  organizationId: string;
  departmentId: string | null;
  siteId: string | null;
  assigneeId: string | null;
  teamId: string | null;
  /** Nível de suporte atual: 1=N1, 2=N2, 3=N3. Null quando a equipe não tem tier configurado. */
  supportTier: number | null;

  categoryId: string | null;
  subcategoryId: string | null;
  serviceId: string | null;
  ciId: string | null;

  impact: Nivel;
  urgency: Nivel;
  priority: Nivel;
  priorityOverridden: boolean;

  statusId: string;
  statusCategory: CategoriaDeStatus;
  resolutionNote: string | null;

  /**
   * A versão de workflow em que este chamado está, e o estado em que parou.
   *
   * Nulos quando nenhum fluxo se aplica — o caso da maioria dos chamados. A
   * versão fica **no chamado**: publicar a v2 de um fluxo não move quem está
   * na v1, e é por isso que o par mora aqui e não é derivado da definição.
   *
   * Enquanto forem nulos, a situação muda por `POST /tickets/:id/status`. A
   * partir do vínculo, aquele caminho responde 409 e o que vale são as
   * transições.
   */
  workflowVersionId: string | null;
  workflowStateKey: string | null;

  openedAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  dueAt: string | null;
  reopenCount: number;

  contractId: string | null;
  contractTermId: string | null;
  coverage: Coverage;

  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface PaginaDeChamados {
  items: Chamado[];
  nextCursor: string | null;
}

export interface Rollup {
  childCount: number;
  openChildCount: number;
  progressPct: number;
  consolidated: string;
}

export interface ArvoreDeChamado {
  root: Chamado;
  descendants: Chamado[];
  rollup: Rollup;
}

export interface VeredictoDeFechamento {
  allowed: boolean;
  reason?: string;
}

export type VisibilidadeDeComentario = 'PUBLIC' | 'INTERNAL' | 'SYSTEM';

export interface Comentario {
  id: string;
  ticketId: string;
  authorId: string | null;
  bodyHtml: string;
  bodyText: string;
  visibility: VisibilidadeDeComentario;
  parentCommentId: string | null;
  createdAt: string;
  editedAt: string | null;
}

export interface RelogioDeSla {
  id: string;
  kind: 'SLA' | 'OLA' | 'UC';
  metric: string;
  policyKey: string;
  policyName: string;
  targetMinutes: number;
  elapsedMinutes: number;
  remainingMinutes: number;
  /** Pode passar de 1: é quanto do prazo foi consumido, não uma barra limitada. */
  consumedFraction: number;
  status: 'RUNNING' | 'PAUSED' | 'STOPPED';
  dueAt: string | null;
  warnAt: string | null;
  breachedAt: string | null;
  outcome: 'MET' | 'BREACHED' | 'VOIDED' | null;
}

export interface Lancamento {
  id: string;
  ticketId: string;
  userId: string;
  startedAt: string;
  minutes: number;
  billable: boolean;
  note: string | null;
  createdAt: string;
}

export interface TotalDeTempo {
  minutes: number;
  billableMinutes: number;
  entries: number;
}

export interface ResumoDeTempo {
  own: TotalDeTempo;
  subtree: TotalDeTempo;
}

export type StatusDeVarredura = 'PENDING' | 'CLEAN' | 'INFECTED' | 'FAILED';

export interface Anexo {
  id: string;
  ticketId: string;
  commentId: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  scanStatus: StatusDeVarredura;
  scannedAt: string | null;
  uploadedById: string | null;
  createdAt: string;
}

// ── rótulos ────────────────────────────────────────────────────────────────

export const ROTULO_DE_TIPO: Record<TipoDeChamado, string> = {
  INCIDENT: 'Incidente',
  REQUEST: 'Requisição',
  PROBLEM: 'Problema',
  CHANGE: 'Mudança',
  TASK: 'Tarefa',
};

export const ROTULO_DE_NIVEL: Record<Nivel, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

export const ROTULO_DE_STATUS: Record<CategoriaDeStatus, string> = {
  NEW: 'Novo',
  IN_PROGRESS: 'Em andamento',
  PENDING: 'Pendente',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
  CANCELLED: 'Cancelado',
};

export const ROTULO_DE_VARREDURA: Record<StatusDeVarredura, string> = {
  PENDING: 'Em varredura',
  CLEAN: 'Liberado',
  INFECTED: 'Infectado',
  FAILED: 'Falha na varredura',
};

export interface Escalonamento {
  id: string;
  ticketId: string;
  fromTeamId: string | null;
  toTeamId: string;
  reason: string | null;
  automatic: boolean;
  escalatedBy: string | null;
  createdAt: string;
}
